import React, { useState, useEffect } from 'react';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import { useSelectRow } from '../utils/useSelectRow';
import { useNavigate } from 'react-router-dom';
import useDataFetch from '../hooks/useDataFetch';
import { getFields, searchFields } from '../utils/fields';
import { formatDate, formatDateTime } from '../utils/format';
import SearchForm from '../components/SearchForm';
import TableView from '../components/TableView';
import Pagination from '../components/Pagination';
import RelatedMenu from '../components/RelatedMenu';
import CsvUploadButtonOrder from '../components/CsvUploadButtonOrder';
import { Stack, Button, Checkbox, Link, Typography, Paper } from "@mui/material";
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Smartphone, LocalShipping } from '@mui/icons-material';
const PickingPage = ({ hasLimit }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    picking: false,
    complete: false,
    cancel: {},
  });
  const [devices, setDevices] = useState([]);
  useEffect(() => {
    let isMounted = true;
    const fetchDevices = async () => {
      try {
        const res = await axiosInstance.get("/picking/devices", { params: { activeOnly: true } });
        if (isMounted) setDevices(res.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error(err);
      }
    };
    fetchDevices();

    return () => { isMounted = false; };
  }, []);
  // useOrdersFetch フックを使用してデータ取得・管理
  const {
    dataset,
    error,
    currentPage,
    totalPages,
    searchQuery,
    handleSearchChange,
    handleSearchSubmit,
    handleClearSearch,
    handlePageChange,
    refetch
  } = useDataFetch('/order/picking', { is_completed: false, order_numbers: '', client_name: '', dates: '', order_status: [4, 5] });

  // useSelectRow フックを使って選択状態を管理
  const { selectedItems, setSelectedItems, handleRowSelect, handleSelectAll } = useSelectRow('order_id');

  // 検索フィールド定義
  const fieldsToInclude = ['dates', 'order_numbers', 'client_name'];
  let searchItems = getFields(searchFields, fieldsToInclude);
  useEffect(() => {
    setSelectedItems(prev =>
      prev.filter(id => dataset.some(item => item.order_id === id))
    );
  }, [dataset]);
  // カラム定義
  const columns = [
    {
      label: (
        <Checkbox
          checked={selectedItems.length === dataset.length}
          indeterminate={selectedItems.length > 0 && selectedItems.length < dataset.length}
          onChange={(e) => handleSelectAll(e, dataset)}
        />
      ),
      accessor: 'checkbox',
      render: (item) => {
        //if (item.order_status_id === 5) return null; // ステータス5なら非表示
        return (
          <Checkbox
            checked={selectedItems.includes(item.order_id)}
            onChange={() => handleRowSelect(item.order_id)}
          />
        );
      },
      width: '50px',
    },
    {
      label: '注文日',
      accessor: 'order_date',
      format: (date) => formatDateTime(date),
      width: '160px',
    },
    {
      label: '注文番号',
      accessor: 'order_number',
      render: (item) => (
        <Link
          component="button"
          onClick={() => navigate(`/orders/${item.order_id}`)}>
          {item.order_number}
        </Link>
      ),
      width: '180px',
    },
    {
      label: 'ステータス',
      accessor: 'order_status_name',
      width: '170px',
    },
    {
      label: '納品希望日',
      accessor: 'delivery_date',
      format: (date) => formatDate(date),
      width: '160px',
    },
    {
      label: '会社名＋事業所名',
      accessor: 'client_name',
      render: (item) => `${item.client_name} ${item.branch_name}`,
      width: '250px',
    },
    {
      label: '倉庫',
      accessor: 'warehouse_name',
      width: '100px',
    },
    {
      label: '端末',
      accessor: 'device_name',
      width: '100px',
    },
    {
      render: (item) => {
        if (item.order_status_id === 5) {
          return (
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleCancelStatus(item.order_id)}
              disabled={!!loading.cancel[item.order_id]}
            >
              取消
            </Button>
          );
        }
        return null;
      },
      width: '64px',
    }
  ];

  // ピッキング送信
  const handlePickingStatus = async () => {
    if (loading.picking || selectedItems.length === 0) {
      if (selectedItems.length === 0) showSnackbar('注文を選択してください', 'warning');
      return;
    }
    setLoading(prev => ({ ...prev, picking: true }));
    try {
      await Promise.all(
        selectedItems.map(orderId =>
          axiosInstance.post('/picking/insert', { order_id: orderId, status: 'READY' })
        )
      );
      showSnackbar('ピッキング端末に送信しました。', 'success');
      setSelectedItems([]);
      refetch(currentPage);
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err?.message || 'ステータス変更に失敗しました', 'error');
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, picking: false }));
    }
  };

  // 発送待ち
  const handleCompleteStatus = async () => {
    if (loading.complete || selectedItems.length === 0) {
      if (selectedItems.length === 0) showSnackbar('注文を選択してください', 'warning');
      return;
    }
    setLoading(prev => ({ ...prev, complete: true }));
    try {
      const res = await axiosInstance.post('/order/update-status', {
        orderIds: selectedItems,
        status: 6,
      });
      showSnackbar(res?.data?.message || 'ステータスを変更しました', 'success');
      setSelectedItems([]);
      refetch(currentPage);
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err?.message || 'ステータス変更に失敗しました', 'error');
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, complete: false }));
    }
  };

  // 取消処理
  const handleCancelStatus = async (orderId) => {
    if (!orderId || loading.cancel[orderId]) {
      if (!orderId) showSnackbar('注文が選択されていません', 'warning');
      return;
    }
    setLoading(prev => ({ ...prev, cancel: { ...prev.cancel, [orderId]: true } }));
    try {
      await axiosInstance.post('/picking/cancel', { order_id: orderId });
      const res = await axiosInstance.post('/order/update-status', {
        orderIds: [orderId],
        status: 4,
      });
      showSnackbar(res?.data?.message || '選択した注文のピッキングリストを取り消しました。', 'success');
      setSelectedItems([]);
      refetch(currentPage);
    } catch (err) {
      showSnackbar(err?.response?.data?.message || err?.message || '取り消し処理に失敗しました', 'error');
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, cancel: { ...prev.cancel, [orderId]: false } }));
    }
  };

  const links = [
    { path: '/orders/new', label: '新規注文作成', icon: 'library_add' },
    { path: '/inventory', label: '在庫一覧', icon: 'inventory_2' },
    { path: '/orders/complete', label: '注文完了処理', icon: 'library_add_check' },
  ];

  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Typography variant="h1" component="h1">ピッキング一覧</Typography>
      <div className='grid2-2_1'>
        {/* 検索フォーム */}
        <SearchForm
          searchQuery={searchQuery}
          searchFields={searchItems}
          handleSearchChange={handleSearchChange}
          handleSearchSubmit={handleSearchSubmit}
          handleClearSearch={handleClearSearch}
        />
        <RelatedMenu links={links}>
          {hasLimit('csv_order_upload') &&
            <CsvUploadButtonOrder buttonStyle="relatedmenu font-m" />
          }
        </RelatedMenu>
      </div>
      <Paper variant='section'>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Inventory2Icon color='primary' />
          <Typography component='h2' variant='h4'>ピッキングの処理状況</Typography>
        </Stack>

        {/* テーブル */}
        <TableView dataset={dataset} columns={columns} keyColumn="order_id" />

        {/* ページネーション */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        {/* ボタンエリア */}
        <div className='button-row'>
          <Button
            variant="contained"
            onClick={handlePickingStatus}
            startIcon={<Smartphone />}
            disabled={!hasLimit('max_picking_devices') || loading.picking}
          >
            ピッキング端末に送信
          </Button>
          <Button
            variant="contained"
            onClick={handleCompleteStatus}
            startIcon={<LocalShipping />}
            disabled={loading.complete}
          >
            発送待ちにする
          </Button>
        </div>
      </Paper>
    </>
  );
};

export default PickingPage;
